import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, DesignV2Scope, Dialog, EmptyState, Skeleton } from "../../design-v2";
import "./settings.css";
import {
  getUserSettings,
  updateTrainingProfileSettings,
  updateWorkoutPreferences,
} from "../../services/api";
import useAuthBootstrap from "../../auth/useAuthBootstrap";
import { workoutLogStore } from "../workoutLog/store/localExecutionStore";
import SettingsMenuScreen from "./SettingsMenuScreen";
import SettingsReadonlyScreen from "./SettingsReadonlyScreen";
import AccountProfileScreen from "./AccountProfileScreen";
import SettingsStackHeader from "./SettingsStackHeader";
import TrainingProfileSectionScreen from "./TrainingProfileSectionScreen";
import WorkoutPreferencesScreen from "./WorkoutPreferencesScreen";
import {
  SETTINGS_ROOT_GROUPS,
  SETTINGS_ROOT_ITEMS,
  TRAINING_PROFILE_MENU_GROUPS,
  TRAINING_PROFILE_MENU_ITEMS,
  findSettingsRootItem,
  findTrainingProfileMenuItem,
  getTrainingProfileHasErrors,
  getTrainingProfileSectionErrorMap,
  resolveSettingsMenuGroups,
} from "./settingsOptions";
import {
  createInitialNavigationStack,
  getCurrentScreen,
  isTrainingProfileScreen,
  popScreenEntry,
  pushScreenEntry,
  SETTINGS_SCREEN_IDS,
} from "./settingsNavigation";
import {
  areTrainingProfilesEqual,
  createTrainingProfileDraft,
  deepClone,
  formatReadonlyValue,
  mapApiErrorDetails,
  mergeLocalIncompletePainIssues,
  rebaseTrainingProfileDraft,
  serializePersistableTrainingProfileDraft,
  toTrainingProfilePayload,
} from "./settingsMappers";
import { validateTrainingProfileDraft } from "./settingsValidation";

const AUTOSAVE_DEBOUNCE_MS = 700;
const TRAINING_PROFILE_CONFLICT_MESSAGE =
  "Your Training Profile was changed somewhere else. We loaded the latest version: please make your change again.";
const SAVED_FLASH_MS = 2500;
const WORKOUT_PREFERENCES_STEPPER_DEBOUNCE_MS = 250;

function applyWorkoutPreferencePatches(basePreferences, pendingEntries) {
  return pendingEntries.reduce(
    (preferences, entry) => ({ ...preferences, ...entry.patch }),
    { ...(basePreferences || {}) }
  );
}

// Phase 6 freshness (§14.5): the server-confirmed preferences replace the
// Workout Log preference cache that the execution screen reads (and observes,
// 6E), so the next rest anchor and Keep Screen Awake use them. Only a
// successful PATCH response is published; a cache failure never fails the save.
function publishSavedWorkoutPreferences(userId, saved) {
  if (!userId || !saved) return;
  void workoutLogStore.cachePreferences(userId, saved).catch(() => {});
}

function ReadonlyRow({ label, value }) {
  return (
    <div className="lz-settings-readonly-row">
      <dt className="lz-settings-readonly-row__label">{label}</dt>
      <dd className="lz-settings-readonly-row__value">{value}</dd>
    </div>
  );
}

function StatusBanner({ tone = "neutral", title, children }) {
  const toneClassName =
    tone === "error"
      ? "lz-settings-banner--error"
      : tone === "success"
        ? "lz-settings-banner--success"
        : "";

  return (
    <div className={["lz-settings-banner", toneClassName].filter(Boolean).join(" ")}>
      {title ? <p className="lz-settings-banner__title">{title}</p> : null}
      <div className="lz-settings-banner__body">{children}</div>
    </div>
  );
}

// Unknown Settings content renders as skeleton shapes rather than fabricated
// values (Rule: never render a placeholder value the API has not returned yet).
function SettingsLoadingSkeleton() {
  return (
    <div className="lz-settings-scroll-content" role="status" aria-label="Loading settings">
      {[0, 1, 2].map((groupIndex) => (
        <div key={groupIndex} className="lz-settings-group">
          <Skeleton shape="text" width="30%" />
          <div className="lz-settings-group__rows">
            {[0, 1].map((rowIndex) => (
              <div
                key={rowIndex}
                className="flex items-center gap-3 px-4 py-4"
                style={{ minHeight: "3.75rem" }}
              >
                <Skeleton shape="circle" width={36} />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton shape="text" width="55%" />
                  <Skeleton shape="text" width="80%" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function serializeTrainingProfileDraft(trainingProfileDraft) {
  return serializePersistableTrainingProfileDraft(trainingProfileDraft);
}

function serializeRawTrainingProfileDraft(trainingProfileDraft) {
  return JSON.stringify(trainingProfileDraft || null);
}

const LINK_NOTICE_COPY = Object.freeze({
  google_connected: { tone: "success", title: "Google connected", body: "Google is now connected to your account." },
  google_already_connected: { tone: "success", title: "Google already connected", body: "This Google account was already connected." },
  identity_conflict: { tone: "error", title: "Google not connected", body: "That Google account is already connected elsewhere." },
  link_cancelled: { tone: "neutral", title: "Connection cancelled", body: "No account changes were made." },
  link_state_invalid: { tone: "error", title: "Connection expired", body: "Please start the connection again." },
  link_provider_error: { tone: "error", title: "Google unavailable", body: "Google could not be connected. Please try again." },
  link_session_changed: { tone: "error", title: "Session changed", body: "Sign in again before connecting Google." },
});

export default function SettingsDrawer({
  isOpen,
  onClose,
  initialScreen,
  notice,
  closeOnInitialBack = false,
}) {
  const [settingsData, setSettingsData] = useState(null);
  const [trainingProfileDraft, setTrainingProfileDraft] = useState(null);
  const [initialTrainingProfileDraft, setInitialTrainingProfileDraft] = useState(null);
  const [navigationStack, setNavigationStack] = useState(() => (
    createInitialNavigationStack(initialScreen)
  ));
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isAutoSavePending, setIsAutoSavePending] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [autosaveError, setAutosaveError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formErrors, setFormErrors] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Phase 11B closure corrective: replaces the native `window.confirm` that
  // guarded closing the drawer over changes which could not be saved.
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [showSavedFeedback, setShowSavedFeedback] = useState(false);
  const [isWorkoutPreferencesSavePending, setIsWorkoutPreferencesSavePending] = useState(false);
  const [workoutPreferencesError, setWorkoutPreferencesError] = useState("");
  const { user } = useAuthBootstrap();
  const userIdRef = useRef(null);
  userIdRef.current = user?.id || null;
  const autosaveTimerRef = useRef(null);
  const savedFlashTimerRef = useRef(null);
  const latestDraftRef = useRef(null);
  // The trainingProfile.revision the baseline draft was loaded or saved at,
  // sent as the save's precondition.
  const trainingProfileRevisionRef = useRef(null);
  const inFlightSnapshotRef = useRef("");
  const queuedSnapshotRef = useRef("");
  const lastFailedSnapshotRef = useRef("");
  const closeAfterSaveRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const workoutPreferencesBaseRef = useRef(null);
  const workoutPreferencesPendingRef = useRef([]);
  const workoutPreferencesInFlightRef = useRef(false);
  const workoutPreferencesInFlightRequestRef = useRef(null);
  const workoutPreferencesGenerationRef = useRef(0);
  const workoutPreferencesNextMutationIdRef = useRef(0);

  const currentScreen = getCurrentScreen(navigationStack);
  const screenParams = currentScreen?.params || {};
  const canGoBack = currentScreen?.id !== SETTINGS_SCREEN_IDS.ROOT;
  const trainingProfileSectionErrorMap = useMemo(
    () => getTrainingProfileSectionErrorMap(fieldErrors),
    [fieldErrors]
  );
  const hasTrainingProfileErrors = useMemo(
    () => getTrainingProfileHasErrors(fieldErrors),
    [fieldErrors]
  );
  const trainingProfileAvailabilityOptions = useMemo(
    () => settingsData?.trainingProfile?.options?.availability || {},
    [settingsData]
  );
  const settingsRootGroups = useMemo(
    () => resolveSettingsMenuGroups(SETTINGS_ROOT_GROUPS, SETTINGS_ROOT_ITEMS),
    []
  );
  const trainingProfileMenuGroups = useMemo(
    () => resolveSettingsMenuGroups(TRAINING_PROFILE_MENU_GROUPS, TRAINING_PROFILE_MENU_ITEMS),
    []
  );

  const hasUnsavedChanges = useMemo(
    () =>
      Boolean(trainingProfileDraft) &&
      Boolean(initialTrainingProfileDraft) &&
      !areTrainingProfilesEqual(trainingProfileDraft, initialTrainingProfileDraft),
    [initialTrainingProfileDraft, trainingProfileDraft]
  );

  const autosaveStatus = useMemo(() => {
    const isWorkoutPreferencesScreen =
      currentScreen?.id === SETTINGS_SCREEN_IDS.SECTION &&
      screenParams.sectionId === "workoutPreferences";
    if (isWorkoutPreferencesScreen) {
      if (workoutPreferencesError) {
        return { label: "Could not save", tone: "error" };
      }
      if (isWorkoutPreferencesSavePending) {
        return { label: "Saving...", tone: "neutral" };
      }
      if (showSavedFeedback === "workoutPreferences" && lastSavedAt) {
        return { label: "Saved", tone: "success" };
      }
      return null;
    }
    if (autosaveError) {
      return { label: "Could not save", tone: "error" };
    }

    if (isAutoSavePending || isAutoSaving) {
      return { label: "Saving...", tone: "neutral" };
    }

    if (showSavedFeedback === "trainingProfile" && lastSavedAt) {
      return { label: "Saved", tone: "success" };
    }

    return null;
  }, [
    autosaveError,
    currentScreen?.id,
    isAutoSavePending,
    isAutoSaving,
    isWorkoutPreferencesSavePending,
    lastSavedAt,
    screenParams.sectionId,
    showSavedFeedback,
    workoutPreferencesError,
  ]);

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  const clearSavedFlashTimer = useCallback(() => {
    if (savedFlashTimerRef.current) {
      window.clearTimeout(savedFlashTimerRef.current);
      savedFlashTimerRef.current = null;
    }
  }, []);

  const showSavedFlash = useCallback((scope = "trainingProfile") => {
    clearSavedFlashTimer();
    setShowSavedFeedback(scope);
    savedFlashTimerRef.current = window.setTimeout(() => {
      setShowSavedFeedback(false);
      savedFlashTimerRef.current = null;
    }, SAVED_FLASH_MS);
  }, [clearSavedFlashTimer]);

  const clearUiFeedback = useCallback(() => {
    clearSavedFlashTimer();
    setAutosaveError("");
    setShowSavedFeedback(false);
  }, [clearSavedFlashTimer]);

  const clearValidationState = useCallback(() => {
    setFieldErrors({});
    setFormErrors([]);
  }, []);

  const clearAllTimers = useCallback(() => {
    clearAutosaveTimer();
    clearSavedFlashTimer();
  }, [clearAutosaveTimer, clearSavedFlashTimer]);

  const resetAutosaveRuntime = useCallback(() => {
    setIsAutoSavePending(false);
    setIsAutoSaving(false);
    inFlightSnapshotRef.current = "";
    queuedSnapshotRef.current = "";
    lastFailedSnapshotRef.current = "";
    closeAfterSaveRef.current = false;
  }, []);

  const resetWorkoutPreferencesRuntime = useCallback(() => {
    workoutPreferencesGenerationRef.current += 1;
    workoutPreferencesPendingRef.current.forEach((entry) => {
      if (entry.timerId) {
        window.clearTimeout(entry.timerId);
      }
    });
    workoutPreferencesPendingRef.current = [];
    workoutPreferencesInFlightRef.current = false;
    workoutPreferencesBaseRef.current = null;
  }, []);

  const resetToRoot = useCallback(() => {
    setNavigationStack(createInitialNavigationStack(initialScreen));
  }, [initialScreen]);

  const finalizeClose = useCallback(() => {
    clearAllTimers();
    resetAutosaveRuntime();
    clearUiFeedback();
    // The discard dialog must never survive the close it was asked about, or a
    // later reopen would start with it already on screen.
    setDiscardConfirmOpen(false);
    onClose();
  }, [clearAllTimers, clearUiFeedback, onClose, resetAutosaveRuntime]);

  const discardLocalChanges = useCallback(() => {
    if (!initialTrainingProfileDraft) {
      return;
    }

    const baselineDraft = deepClone(initialTrainingProfileDraft);
    latestDraftRef.current = baselineDraft;
    setTrainingProfileDraft(baselineDraft);
    clearValidationState();
    clearUiFeedback();
    setLastSavedAt(null);
    setWorkoutPreferencesError("");
    clearAllTimers();
    resetAutosaveRuntime();
  }, [
    clearAllTimers,
    clearUiFeedback,
    clearValidationState,
    initialTrainingProfileDraft,
    resetAutosaveRuntime,
  ]);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    clearAllTimers();
    resetAutosaveRuntime();
    resetWorkoutPreferencesRuntime();
    clearValidationState();
    clearUiFeedback();
    setLastSavedAt(null);
    setIsWorkoutPreferencesSavePending(false);
    setWorkoutPreferencesError("");

    try {
      const response = await getUserSettings();
      const nextDraft = createTrainingProfileDraft(response);

      latestDraftRef.current = nextDraft;
      trainingProfileRevisionRef.current = response.trainingProfile?.revision || null;
      workoutPreferencesBaseRef.current = response.workoutPreferences || null;
      setSettingsData(response);
      setTrainingProfileDraft(nextDraft);
      setInitialTrainingProfileDraft(deepClone(nextDraft));
      resetToRoot();
    } catch (error) {
      setLoadError(error?.message || "Unable to load settings.");
    } finally {
      setIsLoading(false);
    }
  }, [
    clearAllTimers,
    clearUiFeedback,
    clearValidationState,
    resetAutosaveRuntime,
    resetWorkoutPreferencesRuntime,
    resetToRoot,
  ]);

  const runAutosave = useCallback(
    async ({ closeAfterSave = false } = {}) => {
      const currentDraft = latestDraftRef.current;
      const baselineSnapshot = serializeTrainingProfileDraft(initialTrainingProfileDraft);
      const currentSnapshot = serializeTrainingProfileDraft(currentDraft);
      const currentRawSnapshot = serializeRawTrainingProfileDraft(currentDraft);
      const shouldCloseAfterSave = closeAfterSave || closeAfterSaveRef.current;

      clearAutosaveTimer();
      setIsAutoSavePending(false);

      if (!currentDraft || currentSnapshot === baselineSnapshot) {
        if (shouldCloseAfterSave) {
          closeAfterSaveRef.current = false;
          finalizeClose();
        }
        return;
      }

      const validation = validateTrainingProfileDraft(
        currentDraft,
        trainingProfileAvailabilityOptions
      );
      if (!validation.ok) {
        if (shouldCloseAfterSave) {
          closeAfterSaveRef.current = false;
        }
        return;
      }

      if (currentSnapshot === lastFailedSnapshotRef.current) {
        if (shouldCloseAfterSave) {
          closeAfterSaveRef.current = false;
        }
        return;
      }

      if (inFlightSnapshotRef.current) {
        queuedSnapshotRef.current = currentSnapshot;
        if (closeAfterSave) {
          closeAfterSaveRef.current = true;
        }
        return;
      }

      if (closeAfterSave) {
        closeAfterSaveRef.current = true;
      }

      inFlightSnapshotRef.current = currentSnapshot;
      setIsAutoSaving(true);
      setAutosaveError("");
      setShowSavedFeedback(false);

      try {
        const response = await updateTrainingProfileSettings(
          toTrainingProfilePayload(currentDraft),
          { expectedRevision: trainingProfileRevisionRef.current }
        );
        trainingProfileRevisionRef.current = response.trainingProfile?.revision || null;
        const responseDraft = createTrainingProfileDraft(response);
        const latestSnapshotNow = serializeTrainingProfileDraft(latestDraftRef.current);
        const latestRawSnapshotNow = serializeRawTrainingProfileDraft(latestDraftRef.current);
        const responseSnapshot = serializeTrainingProfileDraft(responseDraft);
        const hasNewerLocalDraft =
          latestSnapshotNow !== currentSnapshot || latestRawSnapshotNow !== currentRawSnapshot;
        const nextResponseDraft =
          latestRawSnapshotNow !== currentRawSnapshot
            ? mergeLocalIncompletePainIssues(responseDraft, latestDraftRef.current)
            : responseDraft;

        setSettingsData(response);
        setInitialTrainingProfileDraft(deepClone(responseDraft));
        setLastSavedAt(Date.now());
        lastFailedSnapshotRef.current = "";

        if (!hasNewerLocalDraft) {
          latestDraftRef.current = nextResponseDraft;
          setTrainingProfileDraft(nextResponseDraft);
          clearValidationState();
          clearUiFeedback();
          showSavedFlash();

          if (
            closeAfterSaveRef.current &&
            latestSnapshotNow === currentSnapshot &&
            responseSnapshot === serializeTrainingProfileDraft(responseDraft)
          ) {
            closeAfterSaveRef.current = false;
            finalizeClose();
            return;
          }
        } else {
          queuedSnapshotRef.current = latestSnapshotNow;
        }
      } catch (error) {
        if (error?.status === 409 && error?.code === "TRAINING_PROFILE_CONFLICT") {
          // The profile changed elsewhere (another tab, the AI Builder): nothing
          // was saved. Reload it and re-apply the local edits section by section;
          // the autosave effect then saves the rebased draft at the new revision.
          queuedSnapshotRef.current = "";
          try {
            const latest = await getUserSettings();
            const latestDraft = createTrainingProfileDraft(latest);
            const { draft, overlapping } = rebaseTrainingProfileDraft({
              baseline: initialTrainingProfileDraft,
              local: latestDraftRef.current,
              latest: latestDraft,
            });

            trainingProfileRevisionRef.current = latest.trainingProfile?.revision || null;
            lastFailedSnapshotRef.current = "";
            latestDraftRef.current = draft;
            setSettingsData(latest);
            setInitialTrainingProfileDraft(deepClone(latestDraft));
            setTrainingProfileDraft(draft);
            if (overlapping) {
              // Nothing is left unsaved (the draft is the latest profile), so
              // this is a notice, not an autosave error that would block closing.
              closeAfterSaveRef.current = false;
              setFormErrors([TRAINING_PROFILE_CONFLICT_MESSAGE]);
            }
          } catch {
            lastFailedSnapshotRef.current = currentSnapshot;
            closeAfterSaveRef.current = false;
            setAutosaveError(TRAINING_PROFILE_CONFLICT_MESSAGE);
            setFormErrors([TRAINING_PROFILE_CONFLICT_MESSAGE]);
          }
          return;
        }

        const latestSnapshotNow = serializeTrainingProfileDraft(latestDraftRef.current);

        if (latestSnapshotNow === currentSnapshot) {
          const apiErrors = mapApiErrorDetails(error?.details);

          lastFailedSnapshotRef.current = currentSnapshot;
          setAutosaveError(error?.message || "Could not save");
          setShowSavedFeedback(false);
          setFieldErrors(apiErrors.fieldErrors);
          setFormErrors(apiErrors.formErrors);
          closeAfterSaveRef.current = false;
        }
      } finally {
        const nextQueuedSnapshot = queuedSnapshotRef.current;

        inFlightSnapshotRef.current = "";
        queuedSnapshotRef.current = "";
        setIsAutoSaving(false);

        if (
          nextQueuedSnapshot &&
          nextQueuedSnapshot !== serializeTrainingProfileDraft(initialTrainingProfileDraft) &&
          nextQueuedSnapshot !== lastFailedSnapshotRef.current
        ) {
          void runAutosave();
        }
      }
    },
    [
      clearAutosaveTimer,
      clearUiFeedback,
      clearValidationState,
      finalizeClose,
      initialTrainingProfileDraft,
      showSavedFlash,
      trainingProfileAvailabilityOptions,
    ]
  );

  useEffect(() => {
    latestDraftRef.current = trainingProfileDraft;
  }, [trainingProfileDraft]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      // A canonical Dialog opened inside Settings handles Escape on `document`
      // and marks the event handled. Without this guard the same key press
      // would bubble on to `window` and also close the drawer behind it.
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        requestClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [isOpen, currentScreen?.id, screenParams.sectionId, screenParams.trainingProfileSectionId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    resetToRoot();
  }, [isOpen, resetToRoot]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // One load per open (Rule P2: no prefetch, no caching layer). A failed load
    // stops here and waits for the explicit Retry, which clears `loadError`;
    // without that guard this effect re-fired on every failure and hammered
    // /api/settings in a tight loop, so the documented error state was never
    // reachable.
    if (!settingsData && !isLoading && !loadError) {
      loadSettings();
    }
  }, [isLoading, isOpen, loadError, loadSettings, settingsData]);

  useEffect(() => {
    if (!isOpen || !trainingProfileDraft || !initialTrainingProfileDraft) {
      clearAutosaveTimer();
      setIsAutoSavePending(false);
      return;
    }

    if (inFlightSnapshotRef.current) {
      clearAutosaveTimer();
      setIsAutoSavePending(false);
      return;
    }

    const currentSnapshot = serializeTrainingProfileDraft(trainingProfileDraft);
    const baselineSnapshot = serializeTrainingProfileDraft(initialTrainingProfileDraft);

    if (currentSnapshot === baselineSnapshot) {
      clearAutosaveTimer();
      setIsAutoSavePending(false);
      return;
    }

    const validation = validateTrainingProfileDraft(
      trainingProfileDraft,
      trainingProfileAvailabilityOptions
    );
    if (!validation.ok || currentSnapshot === lastFailedSnapshotRef.current) {
      clearAutosaveTimer();
      setIsAutoSavePending(false);
      return;
    }

    clearAutosaveTimer();
    setIsAutoSavePending(true);
    autosaveTimerRef.current = window.setTimeout(() => {
      void runAutosave();
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      clearAutosaveTimer();
      setIsAutoSavePending(false);
    };
  }, [
    clearAutosaveTimer,
    initialTrainingProfileDraft,
    isOpen,
    runAutosave,
    trainingProfileAvailabilityOptions,
    trainingProfileDraft,
  ]);

  // Phase 6E: Back to the workout unmounts the drawer with its route. An edit
  // still waiting in the stepper debounce, or queued behind the in-flight save,
  // is flushed rather than discarded: the unsent entries merge into one PATCH
  // carrying only their latest values, sent once the in-flight save (if any)
  // settles so requests keep their order. Navigation never waits on it, and
  // only its successful response is published.
  const flushWorkoutPreferencesOnUnmount = useCallback(() => {
    const unsent = workoutPreferencesPendingRef.current.filter(
      (entry) => entry.status !== "inFlight"
    );
    if (unsent.length === 0) {
      return;
    }
    const patch = applyWorkoutPreferencePatches({}, unsent);
    const userId = userIdRef.current;
    const inFlight = workoutPreferencesInFlightRef.current
      ? workoutPreferencesInFlightRequestRef.current
      : null;
    void (async () => {
      await Promise.resolve(inFlight).catch(() => {});
      try {
        publishSavedWorkoutPreferences(userId, await updateWorkoutPreferences(patch));
      } catch (_error) {
        // Not saved: the confirmed cached preferences stay authoritative.
      }
    })();
  }, []);

  useEffect(() => () => {
    clearAllTimers();
    flushWorkoutPreferencesOnUnmount();
    resetWorkoutPreferencesRuntime();
  }, [clearAllTimers, flushWorkoutPreferencesOnUnmount, resetWorkoutPreferencesRuntime]);

  function handleDraftChange(nextDraft) {
    const nextSnapshot = serializeTrainingProfileDraft(nextDraft);
    const validation = validateTrainingProfileDraft(
      nextDraft,
      trainingProfileAvailabilityOptions
    );

    latestDraftRef.current = nextDraft;
    setTrainingProfileDraft(nextDraft);
    setFieldErrors(validation.fieldErrors);
    setFormErrors(validation.formErrors);
    setAutosaveError("");
    setShowSavedFeedback(false);

    if (inFlightSnapshotRef.current && nextSnapshot !== inFlightSnapshotRef.current) {
      queuedSnapshotRef.current = nextSnapshot;
    }
  }

  async function processWorkoutPreferencesQueue() {
    if (workoutPreferencesInFlightRef.current) {
      return;
    }

    const entry = workoutPreferencesPendingRef.current[0];
    if (!entry || !entry.ready) {
      return;
    }

    const generation = entry.generation;
    const requestPatch = { ...entry.patch };
    entry.status = "inFlight";
    workoutPreferencesInFlightRef.current = true;

    try {
      const request = updateWorkoutPreferences(requestPatch);
      workoutPreferencesInFlightRequestRef.current = request;
      const saved = await request;
      publishSavedWorkoutPreferences(userIdRef.current, saved);
      if (generation !== workoutPreferencesGenerationRef.current) {
        return;
      }

      workoutPreferencesBaseRef.current = saved;
      workoutPreferencesPendingRef.current = workoutPreferencesPendingRef.current.filter(
        (candidate) => candidate.id !== entry.id
      );
      const hasRemainingChanges = workoutPreferencesPendingRef.current.length > 0;
      setSettingsData((current) => ({
        ...current,
        workoutPreferences: applyWorkoutPreferencePatches(
          saved,
          workoutPreferencesPendingRef.current
        ),
      }));
      setWorkoutPreferencesError("");
      setIsWorkoutPreferencesSavePending(hasRemainingChanges);
      if (!hasRemainingChanges) {
        setLastSavedAt(Date.now());
        showSavedFlash("workoutPreferences");
      }
    } catch (error) {
      if (generation !== workoutPreferencesGenerationRef.current) {
        return;
      }

      workoutPreferencesPendingRef.current = workoutPreferencesPendingRef.current.filter(
        (candidate) => candidate.id !== entry.id
      );
      setIsWorkoutPreferencesSavePending(
        workoutPreferencesPendingRef.current.length > 0
      );
      clearSavedFlashTimer();
      setShowSavedFeedback(false);
      setSettingsData((current) => ({
        ...current,
        workoutPreferences: applyWorkoutPreferencePatches(
          workoutPreferencesBaseRef.current,
          workoutPreferencesPendingRef.current
        ),
      }));
      setWorkoutPreferencesError(error?.message || "Unable to save workout preferences.");
    } finally {
      if (generation === workoutPreferencesGenerationRef.current) {
        workoutPreferencesInFlightRef.current = false;
        void processWorkoutPreferencesQueue();
      }
    }
  }

  function handleWorkoutPreferencesChange(patch, { debounceKey = null } = {}) {
    if (!settingsData?.workoutPreferences || !workoutPreferencesBaseRef.current) {
      return;
    }

    const generation = workoutPreferencesGenerationRef.current;
    let entry = null;

    if (debounceKey) {
      entry = workoutPreferencesPendingRef.current.find(
        (candidate) =>
          candidate.debounceKey === debounceKey &&
          candidate.status === "debouncing"
      );
    }

    if (entry) {
      if (entry.timerId) {
        window.clearTimeout(entry.timerId);
      }
      entry.patch = { ...entry.patch, ...patch };
      workoutPreferencesPendingRef.current = [
        ...workoutPreferencesPendingRef.current.filter(
          (candidate) => candidate.id !== entry.id
        ),
        entry,
      ];
    } else {
      workoutPreferencesNextMutationIdRef.current += 1;
      entry = {
        id: workoutPreferencesNextMutationIdRef.current,
        generation,
        patch: { ...patch },
        debounceKey,
        ready: !debounceKey,
        status: debounceKey ? "debouncing" : "queued",
        timerId: null,
      };
      workoutPreferencesPendingRef.current = [
        ...workoutPreferencesPendingRef.current,
        entry,
      ];
    }

    setSettingsData((current) => ({
      ...current,
      workoutPreferences: {
        ...current.workoutPreferences,
        ...patch,
      },
    }));
    setIsWorkoutPreferencesSavePending(true);
    setWorkoutPreferencesError("");
    clearSavedFlashTimer();
    setShowSavedFeedback(false);

    if (debounceKey) {
      entry.timerId = window.setTimeout(() => {
        if (generation !== workoutPreferencesGenerationRef.current) {
          return;
        }
        entry.timerId = null;
        entry.ready = true;
        entry.status = "queued";
        void processWorkoutPreferencesQueue();
      }, WORKOUT_PREFERENCES_STEPPER_DEBOUNCE_MS);
      return;
    }

    void processWorkoutPreferencesQueue();
  }

  function pushScreen(id, params = {}) {
    setNavigationStack((currentStack) => pushScreenEntry(currentStack, id, params));
  }

  function popScreen() {
    setNavigationStack((currentStack) => popScreenEntry(currentStack));
  }

  function handleBack() {
    const initialDepth = createInitialNavigationStack(initialScreen).length;
    if (closeOnInitialBack && navigationStack.length === initialDepth) {
      requestClose();
      return;
    }
    popScreen();
  }

  function requestClose() {
    const validation = validateTrainingProfileDraft(
      trainingProfileDraft,
      trainingProfileAvailabilityOptions
    );
    const hasLocalValidationError = Boolean(trainingProfileDraft) && !validation.ok;

    if (!hasUnsavedChanges) {
      finalizeClose();
      return;
    }

    // Phase 11B closure corrective: this decision used to be a native
    // `window.confirm`, the last one left on a migrated V2 surface. The branch
    // it guards is unchanged -- only unsaved changes that CANNOT be saved (a
    // local validation error or a failed autosave) reach it, confirming still
    // discards and closes, and declining still leaves the drawer open with the
    // draft intact. The native call was synchronous, so the discard path now
    // lives in the dialog's own confirm handler and this function simply opens
    // it and stops.
    if (hasLocalValidationError || autosaveError) {
      setDiscardConfirmOpen(true);
      return;
    }

    if (isAutoSavePending || isAutoSaving) {
      void runAutosave({ closeAfterSave: true });
      return;
    }

    void runAutosave({ closeAfterSave: true });
  }

  function handleRootItemSelect(item) {
    if (item.screenId === SETTINGS_SCREEN_IDS.TRAINING_PROFILE_MENU) {
      pushScreen(item.screenId);
      return;
    }

    pushScreen(item.screenId, { sectionId: item.id });
  }

  function handleTrainingProfileItemSelect(item) {
    pushScreen(item.screenId, { trainingProfileSectionId: item.id });
  }

  function renderTrainingProfileFeedback() {
    if (!isTrainingProfileScreen(currentScreen) || formErrors.length === 0) {
      return null;
    }

    return (
      <StatusBanner tone="error" title="Validation Issues">
        <ul className="list-disc pl-5">
          {formErrors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </StatusBanner>
    );
  }

  function renderAccountScreen() {
    const account = settingsData?.account || {};
    const profile = account.profile || {};

    // The Account surface owns its own grouped sections, so it is not wrapped
    // in the single read-only card the other section screens share.
    return (
      <AccountProfileScreen
        profile={profile}
        connectedAccounts={account.connectedAccounts}
        auth={account.auth}
        capabilities={account.capabilities}
        onAccountChange={(nextAccount) => {
          setSettingsData((current) => ({
            ...current,
            account: nextAccount,
          }));
        }}
        onRefreshSettings={loadSettings}
        onProfileChange={(nextProfile) => {
          setSettingsData((current) => ({
            ...current,
            account: {
              ...current.account,
              profile: nextProfile,
            },
          }));
        }}
      />
    );
  }

  function renderAiCoachingScreen() {
    const aiCoaching = settingsData?.aiCoaching || {};

    return (
      <SettingsReadonlyScreen
        description="Read-only for V1. No AI Builder or coaching controls are added here."
        showTitle={false}
      >
        <dl>
          <ReadonlyRow label="Mode" value={formatReadonlyValue(aiCoaching.mode)} />
          <ReadonlyRow
            label="Autonomy Level"
            value={formatReadonlyValue(aiCoaching.autonomyLevel)}
          />
        </dl>
      </SettingsReadonlyScreen>
    );
  }

  function renderWorkoutPreferencesScreen() {
    return (
      <div className="space-y-4">
        {workoutPreferencesError ? (
          <StatusBanner tone="error" title="Could not save">
            {workoutPreferencesError}
          </StatusBanner>
        ) : null}
        <WorkoutPreferencesScreen
          preferences={settingsData?.workoutPreferences}
          onChange={handleWorkoutPreferencesChange}
        />
      </div>
    );
  }

  function renderInterfaceScreen() {
    const units = settingsData?.interface?.units || {};

    return (
      <SettingsReadonlyScreen description="Height units are read-only in V1." showTitle={false}>
        <dl>
          <ReadonlyRow label="Height Unit" value={formatReadonlyValue(units.height)} />
        </dl>
      </SettingsReadonlyScreen>
    );
  }

  function renderSettingsSectionScreen() {
    switch (screenParams.sectionId) {
      case "account":
        return renderAccountScreen();
      case "aiCoaching":
        return renderAiCoachingScreen();
      case "workoutPreferences":
        return renderWorkoutPreferencesScreen();
      case "interface":
        return renderInterfaceScreen();
      default:
        return null;
    }
  }

  function renderRootScreen() {
    const rootItemStateMap = {
      trainingProfile: hasTrainingProfileErrors
        ? {
          badge: "Error",
          meta: "Validation issues",
        }
        : autosaveError
          ? {
            meta: "Changes not saved",
          }
          : isAutoSavePending || isAutoSaving
            ? {
              meta: "Saving...",
            }
            : {},
    };

    return (
      <SettingsMenuScreen
        groups={settingsRootGroups}
        itemStateMap={rootItemStateMap}
        onSelect={handleRootItemSelect}
        showTitle={false}
        showIcons
      />
    );
  }

  function renderTrainingProfileMenuScreen() {
    const itemStateMap = TRAINING_PROFILE_MENU_ITEMS.reduce((accumulator, item) => {
      accumulator[item.id] = trainingProfileSectionErrorMap[item.id]
        ? { badge: "Error" }
        : {};
      return accumulator;
    }, {});

    return (
      <div className="space-y-6">
        {renderTrainingProfileFeedback()}
        <SettingsMenuScreen
          groups={trainingProfileMenuGroups}
          itemStateMap={itemStateMap}
          onSelect={handleTrainingProfileItemSelect}
          showTitle={false}
          showIcons={false}
        />
      </div>
    );
  }

  function renderTrainingProfileSectionScreen() {
    const currentItem = findTrainingProfileMenuItem(screenParams.trainingProfileSectionId);

    if (!currentItem) {
      return null;
    }

    return (
      <div className="space-y-6">
        {renderTrainingProfileFeedback()}
        <TrainingProfileSectionScreen
          title={currentItem.label}
          description={currentItem.description}
          sectionId={currentItem.id}
          draft={trainingProfileDraft}
          onChange={handleDraftChange}
          fieldErrors={fieldErrors}
          options={trainingProfileAvailabilityOptions}
          showTitle={false}
        />
      </div>
    );
  }

  function renderCurrentScreen() {
    switch (currentScreen?.id) {
      case SETTINGS_SCREEN_IDS.SECTION:
        return renderSettingsSectionScreen();
      case SETTINGS_SCREEN_IDS.TRAINING_PROFILE_MENU:
        return renderTrainingProfileMenuScreen();
      case SETTINGS_SCREEN_IDS.TRAINING_PROFILE_SECTION:
        return renderTrainingProfileSectionScreen();
      case SETTINGS_SCREEN_IDS.ROOT:
      default:
        return renderRootScreen();
    }
  }

  function getHeaderCopy() {
    if (currentScreen?.id === SETTINGS_SCREEN_IDS.TRAINING_PROFILE_SECTION) {
      const item = findTrainingProfileMenuItem(screenParams.trainingProfileSectionId);
      return {
        eyebrow: "Training Profile",
        title: item?.label || "Training Profile",
      };
    }

    if (currentScreen?.id === SETTINGS_SCREEN_IDS.TRAINING_PROFILE_MENU) {
      return {
        eyebrow: "Settings",
        title: "Training Profile",
      };
    }

    if (currentScreen?.id === SETTINGS_SCREEN_IDS.SECTION) {
      const item = findSettingsRootItem(screenParams.sectionId);
      return {
        eyebrow: "Settings",
        title: item?.label || "Settings",
      };
    }

    return {
      eyebrow: "Settings",
      title: "Settings",
    };
  }

  const headerCopy = getHeaderCopy();

  if (!isOpen) {
    return null;
  }

  return (
    <DesignV2Scope className="lz-settings-overlay">
      <button
        type="button"
        aria-label="Close settings overlay"
        className="lz-settings-backdrop"
        onClick={requestClose}
      />

      <div className="lz-settings-panel">
        <SettingsStackHeader
          eyebrow={headerCopy.eyebrow}
          title={headerCopy.title}
          canGoBack={canGoBack}
          onBack={handleBack}
          onClose={requestClose}
          status={autosaveStatus}
        />

        {isLoading ? (
          <div className="lz-settings-scroll">
            <SettingsLoadingSkeleton />
          </div>
        ) : loadError ? (
          <div className="lz-settings-centered">
            <EmptyState
              titleAs="h2"
              title="Unable to load settings"
              body={loadError}
              action={
                <div className="flex flex-wrap gap-3">
                  <Button variant="secondary" onClick={requestClose}>
                    Close
                  </Button>
                  <Button onClick={loadSettings}>Retry</Button>
                </div>
              }
            />
          </div>
        ) : (
          <div ref={scrollContainerRef} className="lz-settings-scroll">
            <div className="lz-settings-scroll-content">
              {notice && LINK_NOTICE_COPY[notice] ? (
                <StatusBanner
                  tone={LINK_NOTICE_COPY[notice].tone}
                  title={LINK_NOTICE_COPY[notice].title}
                >
                  {LINK_NOTICE_COPY[notice].body}
                </StatusBanner>
              ) : null}
              {renderCurrentScreen()}
            </div>
          </div>
        )}
      </div>

      {/* The exact decision the native confirm posed, unchanged: confirming
          discards the unsaved changes and closes the drawer; declining -- via
          Cancel, Escape, the close button or the backdrop, all of which the
          native dialog also treated as "stay" -- leaves the drawer open with
          the draft untouched. */}
      <Dialog
        size="sm"
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        title="You have changes that could not be saved. Close and discard them?"
        closeLabel="Close discard changes dialog"
        footer={
          <>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setDiscardConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={() => {
                setDiscardConfirmOpen(false);
                discardLocalChanges();
                finalizeClose();
              }}
            >
              Discard
            </Button>
          </>
        }
      />
    </DesignV2Scope>
  );
}

export { LINK_NOTICE_COPY, StatusBanner };
